/**
 * Map a ticket's captured PageUrl to a short, human-readable "feature /
 * area" label so the feedback list can be grouped by which part of the
 * app the bug was reported from.
 *
 * Examples:
 *   /projects/PRJ-1/workorders/:wo/tests/:td?tab=2&stage=1
 *     → "Test Workflow — Analysis & Plots (Stage 1)"
 *   /projects/PRJ-1/workorders/:wo                → "Project → Work Orders"
 *   /projects/PRJ-1/locations?location=PIT-2ND-1  → "Project → Locations & Samples"
 *   /workorders                                   → "Work Orders (global list)"
 *   /admin/feedback                               → "Admin → Feedback"
 */
export function featureFromUrl(url: string | null | undefined): string {
  if (!url) return 'Unknown';
  let pathname = '';
  let search: URLSearchParams | null = null;
  try {
    const u = new URL(url);
    pathname = u.pathname;
    search = u.searchParams;
  } catch {
    // Some tickets might have a pathname-only value — try as relative URL.
    try {
      const u = new URL(url, 'https://x');
      pathname = u.pathname;
      search = u.searchParams;
    } catch {
      return 'Unknown';
    }
  }

  const p = pathname;

  // Test Workflow (tab-aware, stage-aware)
  if (p.includes('/workorders/') && p.includes('/tests/')) {
    const tab = search?.get('tab');
    const stage = search?.get('stage');
    if (tab === '2' && stage) return `Test Workflow — Analysis & Plots (Stage ${stage})`;
    if (tab === '2') return 'Test Workflow — Analysis & Plots';
    if (tab === '1') return 'Test Workflow — Result Entry';
    if (tab === '0' || !tab) return 'Test Workflow — Parameters';
    return 'Test Workflow';
  }

  // Global Work Orders list
  if (p === '/workorders' || p.startsWith('/workorders?') || p === '/workorders/') {
    return 'Work Orders (global list)';
  }

  // Project-scoped pages
  if (p.startsWith('/projects/')) {
    if (p.includes('/workorders')) return 'Project → Work Orders';
    if (p.includes('/locations')) return 'Project → Locations & Samples';
    if (p.includes('/tests')) return 'Project → Tests';
    return 'Project detail';
  }

  const first = p.split('/').filter(Boolean)[0];
  if (!first) return 'Home';

  if (first === 'projects') return 'Projects list';
  if (first === 'quotes') return 'Quotes';
  if (first === 'clients') return 'Clients';
  if (first === 'sites') return 'Sites';
  if (first === 'locations') return 'Locations';
  if (first === 'samples') return 'Samples';
  if (first === 'tests') return 'Tests';
  if (first === 'my-feedback') return 'Feedback (user)';
  if (first === 'admin') {
    const second = p.split('/').filter(Boolean)[1];
    if (second === 'feedback') return 'Admin → Feedback';
    if (second === 'users') return 'Admin → Users';
    if (second === 'analytics') return 'Admin → Analytics';
    if (second === 'test-catalog' || second === 'testCatalog') return 'Admin → Test Catalog';
    if (second === 'approval-tiers' || second === 'approvalTiers') return 'Admin → Approval Tiers';
    return 'Admin';
  }

  // Fallback: title-case the first path segment
  return first.charAt(0).toUpperCase() + first.slice(1).replace(/-/g, ' ');
}
